/**
 * Prontidao para ligar os motores: um checklist DERIVADO da configuracao, com
 * percentual e o proximo passo.
 *
 * Decisoes nao obvias:
 *  1. E funcao pura: recebe a configuracao EFETIVA (o que ja foi salvo, mais o
 *     estado imediato do WhatsApp) e devolve itens. Nao le rascunho: um campo
 *     digitado e ainda nao salvo nao pode marcar um item como pronto.
 *  2. Os itens seguem a ORDEM DO FLUXO real (URL colada -> segredo -> evento
 *     aceito -> funil ligado -> WhatsApp -> voz -> equipe). O "proximo passo"
 *     e o primeiro pendente nessa ordem, porque um passo depende do anterior:
 *     nao adianta reconectar o WhatsApp se nenhum evento chega.
 *  3. O e-mail NAO entra na conta do percentual. A decisao do provedor e do
 *     dono do produto, nao do lojista; contar contra ele seria pedir uma acao
 *     que ele nao consegue fazer. O item aparece como "informativo".
 *  4. Percentual e inteiro (0..100) calculado de duas contagens inteiras, na
 *     exibicao. Nenhum float viaja.
 *  5. Nada aqui sugere lembrar alguem antes do fato: o unico item sobre funil
 *     e "ao menos um funil ligado", e o texto do evento de teste manda deixar
 *     os 30 minutos passarem.
 */

import { formatCount, formatDateTime } from '../../lib/format';
import type { AbaConfiguracoes, CompanySettings, WebhookIntegration, WhatsAppIntegration } from './types';
import { FUNIL_INFO, WEBHOOK_PROVIDER_LABEL } from './vocabulario';

export type ChaveDeProntidao = 'url_colada' | 'segredo' | 'evento_aceito' | 'funil_ligado' | 'whatsapp' | 'voz' | 'equipe' | 'email';

export type EstadoDoItem = 'ok' | 'pendente' | 'informativo';

export interface ItemDeProntidao {
  readonly key: ChaveDeProntidao;
  readonly titulo: string;
  readonly estado: EstadoDoItem;
  /** Como esta agora, com o dado que sustenta ("Kiwify · 24/09/26, 10:12"). */
  readonly detalhe: string;
  /** O que fazer para virar `ok`; `null` quando ja esta ok ou quando a acao nao e do lojista. */
  readonly proximoPasso: string | null;
  readonly aba: AbaConfiguracoes;
  /** `id` da secao para rolar ate ela ao trocar de aba. */
  readonly ancora: string;
}

export interface Prontidao {
  readonly itens: readonly ItemDeProntidao[];
  /** Itens que contam (todos menos os informativos). */
  readonly total: number;
  readonly concluidos: number;
  /** 0..100, inteiro. */
  readonly percentual: number;
  readonly pronto: boolean;
  readonly proximo: ItemDeProntidao | null;
}

function nomesDe(lista: readonly WebhookIntegration[]): string {
  return lista.map((w) => WEBHOOK_PROVIDER_LABEL[w.provider]).join(', ');
}

export function calcularProntidao(settings: CompanySettings, whatsapp: WhatsAppIntegration): Prontidao {
  const webhooks = settings.integrations.webhooks;
  const conectados = webhooks.filter((w) => w.connected);
  const semSegredo = conectados.filter((w) => w.secretState === 'missing');
  const comEvento = conectados.filter((w) => w.lastEventAt !== null);
  const ultimoEvento = comEvento.reduce<WebhookIntegration | null>((melhor, w) => {
    if (melhor === null || (w.lastEventAt ?? '') > (melhor.lastEventAt ?? '')) {
      return w;
    }
    return melhor;
  }, null);
  const rejeitados7d = conectados.reduce((acc, w) => acc + w.rejectedLast7Days, 0);
  const funisLigados = settings.cadence.funnels.filter((f) => f.enabled);
  const voz = settings.integrations.voice;
  const vozPronta = voz.state === 'ready' && voz.assistantName.trim() !== '';
  const equipe = settings.team;
  const donos = equipe.filter((m) => m.role === 'owner').length;
  const equipePronta = equipe.length >= 2 && donos >= 1;
  const emailDecidido = settings.integrations.email.state !== 'awaiting_decision';

  const itens: readonly ItemDeProntidao[] = [
    {
      key: 'url_colada',
      titulo: 'Plataforma conectada',
      estado: conectados.length > 0 ? 'ok' : 'pendente',
      detalhe:
        conectados.length > 0
          ? `${nomesDe(conectados)} ${conectados.length === 1 ? 'envia' : 'enviam'} eventos para o motor.`
          : 'Nenhuma plataforma envia eventos ainda. Sem evento não há abandono, e sem abandono não há o que recuperar.',
      proximoPasso:
        conectados.length > 0 ? null : 'Copie a URL do webhook e cole no painel da plataforma (Kiwify, Hotmart, Shopify) ou use o webhook genérico no seu checkout.',
      aba: 'integracoes',
      ancora: 'webhooks',
    },
    {
      key: 'segredo',
      titulo: 'Segredo de assinatura',
      estado: conectados.length > 0 && semSegredo.length === 0 ? 'ok' : 'pendente',
      detalhe:
        conectados.length === 0
          ? 'Depende de uma plataforma conectada.'
          : semSegredo.length === 0
            ? `${conectados.length === 1 ? 'A plataforma conectada assina' : `As ${formatCount(conectados.length)} plataformas conectadas assinam`} cada evento; o motor confere antes de ler o corpo.`
            : `${nomesDe(semSegredo)} sem segredo: todo evento responde 401 e nada entra na régua${rejeitados7d > 0 ? ` (${formatCount(rejeitados7d)} rejeitados em 7 dias)` : ''}.`,
      proximoPasso:
        conectados.length === 0
          ? 'Conecte uma plataforma primeiro.'
          : semSegredo.length === 0
            ? null
            : 'Peça à administração para configurar o segredo no motor. Sem ele, o motor não confia no evento — e a plataforma não avisa que foi recusada.',
      aba: 'integracoes',
      ancora: 'webhooks',
    },
    {
      key: 'evento_aceito',
      titulo: 'Primeiro evento aceito',
      estado: ultimoEvento !== null ? 'ok' : 'pendente',
      detalhe:
        ultimoEvento !== null
          ? `Último aceito: ${WEBHOOK_PROVIDER_LABEL[ultimoEvento.provider]} · ${formatDateTime(ultimoEvento.lastEventAt)}.`
          : 'Nenhum evento aceito até agora.',
      proximoPasso:
        ultimoEvento !== null
          ? null
          : 'Faça um checkout de teste na plataforma e deixe 30 minutos sem pagar: o evento aparece no histórico em segundos e, passado o prazo, entra na régua.',
      aba: 'integracoes',
      ancora: 'webhooks',
    },
    {
      key: 'funil_ligado',
      titulo: 'Funil ligado',
      estado: funisLigados.length > 0 ? 'ok' : 'pendente',
      detalhe:
        funisLigados.length > 0
          ? `${funisLigados.map((f) => FUNIL_INFO[f.kind].label).join(' e ')} com régua ativa — sempre depois do abandono ou do vencimento.`
          : 'Nenhum funil ligado: nenhum toque sai, mesmo com evento chegando.',
      proximoPasso: funisLigados.length > 0 ? null : 'Ligue ao menos o funil de Carrinho abandonado na aba Régua.',
      aba: 'regua',
      ancora: 'funis-ativos',
    },
    {
      key: 'whatsapp',
      titulo: 'WhatsApp conectado',
      estado: whatsapp.state === 'connected' ? 'ok' : 'pendente',
      detalhe:
        whatsapp.state === 'connected'
          ? `Instância ${whatsapp.instanceName} com sessão ativa${whatsapp.connectedSince === null ? '' : ` desde ${formatDateTime(whatsapp.connectedSince)}`}.`
          : 'Sem sessão: o 2º tier não sai e a régua para depois das ligações.',
      proximoPasso: whatsapp.state === 'connected' ? null : 'Leia o código QR no aparelho da empresa (WhatsApp → Dispositivos conectados).',
      aba: 'integracoes',
      ancora: 'whatsapp',
    },
    {
      key: 'voz',
      titulo: 'Voz configurada',
      estado: vozPronta ? 'ok' : 'pendente',
      detalhe: vozPronta
        ? `“${voz.assistantName.trim()}” liga pelo número de origem.`
        : voz.state === 'pending'
          ? 'Aguardando o número de origem da administração.'
          : 'O assistente está sem nome.',
      proximoPasso: vozPronta
        ? null
        : voz.state === 'pending'
          ? 'A administração ativa o número; enquanto isso, escolha a voz e o nome do assistente e salve.'
          : 'Dê um nome ao assistente e salve.',
      aba: 'integracoes',
      ancora: 'voz',
    },
    {
      key: 'equipe',
      titulo: 'Equipe convidada',
      estado: equipePronta ? 'ok' : 'pendente',
      detalhe:
        equipe.length === 0
          ? 'Ninguém acessa o painel.'
          : equipe.length === 1
            ? 'Só uma pessoa acessa o painel.'
            : `${formatCount(equipe.length)} pessoas com acesso · ${formatCount(donos)} ${donos === 1 ? 'dono' : 'donos'}.`,
      proximoPasso: equipePronta ? null : 'Convide quem vai acompanhar leads e responder no WhatsApp; uma pessoa só vira gargalo.',
      aba: 'equipe',
      ancora: 'equipe',
    },
    {
      key: 'email',
      titulo: 'E-mail de reserva',
      estado: emailDecidido ? 'ok' : 'informativo',
      detalhe: emailDecidido ? 'Provedor de envio definido.' : 'Provedor de envio a definir pelo dono do produto. Não entra na conta: não é ação da empresa.',
      proximoPasso: null,
      aba: 'integracoes',
      ancora: 'email',
    },
  ];

  const contaveis = itens.filter((i) => i.estado !== 'informativo');
  const concluidos = contaveis.filter((i) => i.estado === 'ok').length;
  const total = contaveis.length;
  const percentual = total === 0 ? 0 : Math.round((concluidos * 100) / total);
  const proximo = itens.find((i) => i.estado === 'pendente') ?? null;

  return { itens, total, concluidos, percentual, pronto: concluidos === total, proximo };
}

/** Quantos itens pendentes cada aba tem: vira o contador da aba. */
export function pendentesPorAba(prontidao: Prontidao): Readonly<Partial<Record<AbaConfiguracoes, number>>> {
  const contagem: Partial<Record<AbaConfiguracoes, number>> = {};
  for (const i of prontidao.itens) {
    if (i.estado === 'pendente') {
      contagem[i.aba] = (contagem[i.aba] ?? 0) + 1;
    }
  }
  return contagem;
}
